import { PAGEPATHS } from "components/Common/Constant"
import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { setBreadcrumbItems } from "../../store/actions"
import { connect } from "react-redux"
import DataTableWithPopup from "components/longContentTable";
import {
  Gauge,
  Zap,
  Info,
  Monitor,
  TrendingUp,
  Clock
} from 'lucide-react'
import "./SitePerformance.css"

const SitePerformance = (props) => {
    document.title = "Site Performance";
    // const [scanId, setScanId] = useState(sessionStorage.getItem('scanId'))
    const { scanId: paramScanId } = useParams();
    const [scanId, setScanId] = useState(paramScanId || sessionStorage.getItem('scanId'));
    const [lightHouseDesktopData, setLightHouseDesktopData] = useState({
        columns: [
            {
                label: 'Performance Audit Type',
                field: 'audit',
                sort: 'asc',
                width: 150
            },
            {
                label: 'Score',
                field: 'score',
                sort: 'asc',
                width: 150
            },
            {
                label: 'Display Value',
                field: 'displayValue',
                sort: 'asc',
                width: 150
            },
            {
                label: 'Description',
                field: 'description',
                sort: 'asc',
                width: 150
            },
            {
                label: 'Details',
                field: 'details',
                sort: 'asc',
                width: 150
            },
        ]
    })

    const breadcrumbItems = [
        { title: "Scan List", link: PAGEPATHS.Scanlist },
        { title: sessionStorage.getItem('domain'), link: "#" },
        { title: "Site Performance", link: "#" }
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Site Performance", breadcrumbItems)
    })

    useEffect(() => {
        const getLHDScanList = async () => {
            try {
                const { data } = await axiosInstance.get(`${ApiNames.FETCH_SITE_PERFORMANCE_LHD_SCAN}/${scanId}`)
                data.data = data.data.map(obj => {
                    return Object.fromEntries(
                        Object.entries(obj).map(([key, value]) => [
                            key, typeof value === 'number' || typeof value === 'boolean'
                                ? value.toString()
                                : value === null
                                    ? 'NaN'
                                    : value
                        ])
                    );
                });
                setLightHouseDesktopData((prev) => ({
                    ...prev,
                    columns: prev.columns,
                    rows: data.data
                }))
            } catch (e) {
                console.error(e);
            }
        }
        getLHDScanList()
    }, [scanId])

    // Calculate summary metrics
    const calculateMetrics = () => {
        if (!lightHouseDesktopData.rows || lightHouseDesktopData.rows.length === 0) {
            return { totalAudits: 0, avgScore: 0, passedAudits: 0 };
        }

        const rows = lightHouseDesktopData.rows;
        const totalAudits = rows.length;
        const passedAudits = rows.filter(row => {
            const score = parseFloat(row.score);
            return !isNaN(score) && score >= 0.9;
        }).length;

        const scores = rows.map(row => parseFloat(row.score)).filter(score => !isNaN(score));
        const avgScore = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

        return { totalAudits, avgScore: Math.round(avgScore * 100), passedAudits };
    };

    const metrics = calculateMetrics();

    return (
        <React.Fragment>
            <div className="site-performance-page">
                {/* Page Header */}
                <div className="site-performance-header">
                    <div className="site-performance-header-content">
                        <div className="site-performance-icon-wrapper">
                            <Gauge className="site-performance-icon" />
                        </div>
                        <div className="site-performance-header-text">
                            <h1>Site Performance Metrics</h1>
                            <p>Google Lighthouse performance analysis for desktop experience on {sessionStorage.getItem('domain')}</p>
                        </div>
                    </div>
                </div>

                {/* Info Section */}
                <div className="site-performance-info-box">
                    <div className="site-performance-info-header">
                        <div className="site-performance-info-icon-wrapper">
                            <Info className="site-performance-info-icon" />
                        </div>
                        <h3>About Lighthouse Performance</h3>
                    </div>
                    <div className="site-performance-info-content">
                        <p>
                            Google Lighthouse is an automated tool for improving web page quality. It runs performance audits
                            to measure metrics like page load speed, time to interactive, and other performance indicators.
                        </p>
                        <p>
                            These metrics help identify opportunities to improve user experience and ensure your site
                            meets modern web performance standards.
                        </p>
                    </div>
                </div>

                {/* Summary Metrics */}
                <div className="site-performance-metrics-grid">
                    <div className="site-performance-metric-card">
                        <div className="site-performance-metric-icon-wrapper blue">
                            <Monitor className="site-performance-metric-icon" />
                        </div>
                        <div className="site-performance-metric-content">
                            <h4>Total Audits</h4>
                            <div className="site-performance-metric-value">{metrics.totalAudits}</div>
                            <p className="site-performance-metric-label">Performance checks performed</p>
                        </div>
                    </div>

                    <div className="site-performance-metric-card">
                        <div className="site-performance-metric-icon-wrapper green">
                            <TrendingUp className="site-performance-metric-icon" />
                        </div>
                        <div className="site-performance-metric-content">
                            <h4>Average Score</h4>
                            <div className="site-performance-metric-value">{metrics.avgScore}%</div>
                            <p className="site-performance-metric-label">Mean performance score</p>
                        </div>
                    </div>

                    <div className="site-performance-metric-card">
                        <div className="site-performance-metric-icon-wrapper purple">
                            <Zap className="site-performance-metric-icon" />
                        </div>
                        <div className="site-performance-metric-content">
                            <h4>Passed Audits</h4>
                            <div className="site-performance-metric-value">{metrics.passedAudits}</div>
                            <p className="site-performance-metric-label">Audits scoring ≥90%</p>
                        </div>
                    </div>
                </div>

                {/* Performance Data Table */}
                <div className="site-performance-data-section">
                    <div className="site-performance-section-header">
                        <Clock className="section-header-icon" />
                        <h2>Lighthouse Desktop Performance Audits</h2>
                        <span className="scan-id">Scan ID: {scanId}</span>
                    </div>
                    <div className="site-performance-data-content">
                        {lightHouseDesktopData && lightHouseDesktopData.rows && lightHouseDesktopData.rows.length > 0 ? (
                            <DataTableWithPopup noBottomColumns={false} thirdPartyData={lightHouseDesktopData} />
                        ) : (
                            <div className="site-performance-empty-state">
                                <Gauge className="site-performance-empty-icon" />
                                <p>No Lighthouse performance data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}
export default connect(null, { setBreadcrumbItems })(SitePerformance)